import { ReadTaskSummaryData } from '@common/config/api/client'
import {
  CHECKBOX_WIDTH,
  COLUMN_WIDTH,
  MIN_VISIBLE_TIMEPOINTS,
  VIEWER_OPTIONS,
} from '@features/reader_summary/pages/constants'
import { TableScrollSize } from '@features/reader_summary/pages/summary.types'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ReaderSummaryTable from './ReaderSummaryTable'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useParams } from 'react-router-dom'
import { requestEsignReauthentication } from '@auth/utils/requestEsignReauthentication'
import { useRestoreSigningProcess } from '@auth/hooks/useRestoreSigningProcess'
import { useReadTaskESignMutation } from '../../hooks/useReadTaskESignMutation'
import SplitButton from '@common/components/SplitButton/SplitButton'
import { closeTab, getViewerLogicalName, isTabOpen } from '@common/utils/acrossTabsUtils'

export default function ReaderSummaryLayout({
  timepoints,
  readTaskId,
  read_task_summary_metadata,
  isSignNSendDisabled,
  onClose,
  onFocusChild,
  readerSetEndTime,
  pauseReaderActiveTime,
  isRawDataEnabled,
}: {
  timepoints: ReadTaskSummaryData['timepoints']
  readTaskId: string
  read_task_summary_metadata: ReadTaskSummaryData['read_task_summary_metadata']
  isSignNSendDisabled: boolean
  onClose?: () => void
  onFocusChild?: (index: number) => void
  readerSetEndTime?: (end_time: Date | number) => void
  pauseReaderActiveTime?: () => void
  isRawDataEnabled?: boolean
}) {
  const [tableScrollSize, setTableScrollSize] = useState<TableScrollSize>({
    columnWidth: COLUMN_WIDTH,
    minVisibleTimepoints: MIN_VISIBLE_TIMEPOINTS,
    emptyCellsCount: 0,
    firstTimepointLeftOffset: COLUMN_WIDTH,
    lastTimepointLeftOffset: 0,
    checkboxWidth: CHECKBOX_WIDTH,
    isScrolling: false,
  })
  const [isSigningIn, setIsSigningIn] = useState(false)
  const { user } = useExtendedAuth()
  const { studyId } = useParams<{ studyId: string }>()
  const readTaskSignMutation = useReadTaskESignMutation()

  const { isSigningInProgress } = useRestoreSigningProcess(readTaskSignMutation, {
    successMessage: 'Read task signed and submitted successfully',
    errorMessage: 'Failed to submit read task',
  })

  const getSignerName = () => {
    const firstName = user?.profile.given_name || ''
    const lastName = user?.profile.family_name || ''
    let signerName = `${firstName} ${lastName}`.trim()

    if (!signerName) {
      signerName = user?.profile.email || user?.profile.preferred_username || ''
    }

    return signerName
  }

  const [disabledViewerOptions, setDisabledViewerOptions] = useState<number[] | undefined>([])

  const handleSignAndSend = async () => {
    if (readerSetEndTime && pauseReaderActiveTime) {
      readerSetEndTime(Date.now())
      pauseReaderActiveTime()
    }
    setIsSigningIn(true)

    const logicalName = getViewerLogicalName(readTaskId, 2)
    const tabIsOpen = isTabOpen(logicalName)
    if (tabIsOpen) {
      closeTab(logicalName)
    }

    const payload = {
      type: 'reader_task_submission',
      read_task_submission: {
        adjudication_justification_id: null,
        justification_comment: '',
      },
    }

    sessionStorage.setItem(
      'reader_modal_state',
      JSON.stringify({
        taskId: readTaskId,
      }),
    )

    try {
      await requestEsignReauthentication(payload, {
        queryParams: {
          read_task_id: readTaskId,
          study_id: studyId!,
        },
        signer_name: getSignerName(),
      })
    } catch {
      setIsSigningIn(false)
    }
  }

  const defaultViewerIndex = (() => {
    if (isRawDataEnabled) return 2
    if (read_task_summary_metadata?.type_of_gcis_viewer?.includes('ophtho')) return 3
    return 0
  })()

  useEffect(() => {
    setDisabledViewerOptions([])
    const disabledViewers = []
    if (read_task_summary_metadata?.show_raw_data) {
      disabledViewers.push(0)
    }
    if (!read_task_summary_metadata?.type_of_gcis_viewer?.includes('fusion')) {
      disabledViewers.push(1)
    }
    if (!read_task_summary_metadata?.type_of_gcis_viewer?.includes('ophtho')) {
      disabledViewers.push(3)
    }
    setDisabledViewerOptions(disabledViewers)
  }, [read_task_summary_metadata])

  useEffect(() => {
    const handleResize = () => {
      const scrollBarSize = window.innerWidth - document.body.offsetWidth
      const availableWidth = window.innerWidth - scrollBarSize - 128 - CHECKBOX_WIDTH

      const potentialTimepoints = Math.floor(availableWidth / (COLUMN_WIDTH + 0.4)) - 1
      const newMinVisibleTimepoints = Math.max(potentialTimepoints, 3)

      const newColumnWidth = availableWidth / (newMinVisibleTimepoints + 1) - 0.4

      const totalTimepointColumns = Math.max(newMinVisibleTimepoints, timepoints.length)
      const emptyCellsCount = totalTimepointColumns - timepoints.length
      const firstTimepointLeftOffset = newColumnWidth + emptyCellsCount * newColumnWidth
      const lastTimepointLeftOffset = firstTimepointLeftOffset + (newMinVisibleTimepoints - 1) * newColumnWidth

      const isScrolling = newMinVisibleTimepoints < timepoints.length

      setTableScrollSize((prev) => ({
        ...prev,
        lastTimepointLeftOffset: lastTimepointLeftOffset,
        emptyCellsCount: emptyCellsCount,
        firstTimepointLeftOffset: firstTimepointLeftOffset,
        columnWidth: newColumnWidth,
        minVisibleTimepoints: totalTimepointColumns,
        isScrolling: isScrolling,
      }))
    }

    window.addEventListener('resize', handleResize)

    const observer = new ResizeObserver(handleResize)
    observer.observe(document.body)

    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, [timepoints])

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          zIndex: 20,
        }}
      >
        <Stack
          direction="row"
          paddingX={4}
          justifyContent="space-between"
          alignItems="center"
          height={56}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <Stack>
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
              }}
            >
              {read_task_summary_metadata.timepoint_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Task ID {read_task_summary_metadata.task_pseudo_id}
            </Typography>
          </Stack>
          <Stack>
            <Button variant="text" onClick={onClose}>
              <Typography
                variant="button"
                sx={{
                  color: 'text.primary',
                }}
              >
                Close
              </Typography>
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Stack paddingX={8} paddingTop={10} paddingBottom={10}>
        <ReaderSummaryTable
          timepoints={timepoints}
          readTaskSummaryMetaData={read_task_summary_metadata}
          tableScrollSize={tableScrollSize}
        />
      </Stack>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: '100%',
          zIndex: 20,
        }}
      >
        <Stack
          direction="row"
          paddingX={4}
          justifyContent="space-between"
          alignItems="center"
          height={56}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="left">
            <SplitButton
              options={VIEWER_OPTIONS}
              onFocusChild={onFocusChild}
              disabledOptions={disabledViewerOptions}
              disabled={read_task_summary_metadata.status === 'COMPLETE'}
              isRawDataEnabled={isRawDataEnabled}
              defaultIndex={defaultViewerIndex}
            />
          </Stack>
          <Stack></Stack>
          <Stack>
            <Button
              size="large"
              variant="contained"
              disabled={isSignNSendDisabled || isSigningInProgress || isSigningIn}
              sx={{
                backgroundColor: 'primary.main',
              }}
              onClick={handleSignAndSend}
              startIcon={isSigningInProgress || isSigningIn ? <CircularProgress size={20} color="inherit" /> : null}
            >
              <Typography
                variant="button"
                sx={{
                  color: 'primary.contrastText',
                }}
              >
                {isSigningInProgress || isSigningIn ? 'Signing...' : 'Sign and send'}
              </Typography>
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>
  )
}
